import { toast } from '@/hooks/use-toast'
import axios from 'axios'
import * as Sentry from '@sentry/react'
import axiosRetry from 'axios-retry'
import { displayConnectionStatusToast } from './connectionStatus'

const http = axios.create({
  baseURL: import.meta.env.VITE_NODE_API_URL,
  timeout: 15000,
  timeoutErrorMessage: 'Request timed out',
  adapter: 'fetch'
})

http.interceptors.request.use(async req => {
  const token = await window.Clerk.session?.getToken()
  if (!token) {
    window.Clerk.session?.remove()
    return Promise.reject(new Error('No authentication token available'))
  }

  req.headers.Authorization = `Bearer ${token}`

  const method = req.method?.toLowerCase()
  if (method === 'post' || method === 'put' || method === 'patch') {
    return {
      ...req,
      'axios-retry': { retries: 0 }
    }
  }
  return req
})

http.interceptors.response.use(
  res => {
    if (res?.data === undefined) {
      Sentry.captureMessage('Empty API Response', {
        level: 'warning',
        contexts: {
          http_error: {
            ALL_DATA: res
          }
        }
      })
    }
    if (!res?.data) {
      console.error('undefined response data for url:', res)
    }
    return res.data
  },
  error => {
    const url: string = error.config?.url
    const status = error.response?.status
    const isFEError = status >= 400 && status < 500

    // CANCELLED REQUEST
    if (axios.isCancel(error)) return Promise.reject(error)
    // TIMEOUT/ABORT
    if (['ETIMEDOUT', 'ECONNABORTED'].includes(error.code)) return Promise.reject(error)
    // NO STATUS OR URL NETWORK ISSUE
    if (!status || !url) return Promise.reject(error)

    const errorMSG =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.response?.data ||
      `Server Unreachable ${status}`

    toast({
      description: (
        <div>
          <pre className='text-red-700 font-bold'>
            {typeof errorMSG === 'object' && errorMSG !== null ? JSON.stringify(errorMSG, null, 2) : errorMSG}
          </pre>
        </div>
      )
    })

    try {
      const body = error.config?.data

      const isJsonHeaders = error.config?.headers?.['Content-Type'] === 'application/json'
      const parcedBody = body && isJsonHeaders ? JSON.parse(body) : body

      const hashedUrl = url
        ?.split('/')
        .map(e => (e.length === 30 ? ':hash' : e))
        .join('/')
      error.name = 'API Error'
      error.message = `URL:${hashedUrl} ${error.message}`

      const name = `API Error : ${error.config?.method.toUpperCase()} ${hashedUrl} ${status}`
      Sentry.captureMessage(isFEError ? name : error, {
        level: isFEError ? 'debug' : 'error',
        contexts: {
          http_error: {
            hasJWT: !!error.config?.headers?.Authorization,
            request: {
              headers: error.config?.headers,
              body: parcedBody
            },
            response: {
              headers: error.response?.headers,
              body: error.response?.data,
              status: error.response?.status
            },
            retryCount: error.config?.['axios-retry']?.retryCount,
            isOnline: window.navigator.onLine,
            code: error.code,
            ALL_DATA: error
          }
        },
        tags: {
          API_RESPONSE_MSG: errorMSG
        }
      })
    } catch (e) {
      console.error('Error in HTTP response error try method', e)
      Sentry.captureException(e)
      Sentry.captureMessage(error.name, isFEError ? 'debug' : 'error')
    }

    return Promise.reject(error.response?.data ?? error)
  }
)

// SHOULD RETRY AFTER INTERCEPTOR
axiosRetry(http, {
  shouldResetTimeout: true,
  retryCondition: error => {
    const status = error.response?.status
    return !status || status >= 500
  },
  onRetry: async (_, error) => {
    console.warn('Retry HTTP API', error.config?.url)
    await displayConnectionStatusToast()
  }
})

export default http
